import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import { PersonRemove, Save, CheckCircle, Person, Warning } from '@mui/icons-material';
import { userService } from '../services/userService';
import { groupService } from '../services/groupService';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

const steps = ['Select User', 'Review User Data', 'Offboarding Actions', 'Confirm & Execute'];

export const Offboarding: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userGroups, setUserGroups] = useState<any[]>([]);

  const [offboardingActions, setOffboardingActions] = useState({
    disableAccount: true,
    revokeAllLicenses: true,
    removeFromAllGroups: true,
    resetPassword: true,
    hideFromGAL: true,
    forwardEmail: false,
    forwardEmailTo: '',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      setError(null);
      const response = await userService.getUsers();
      const usersData = getApiCollection<any>(response, 'loading users for offboarding');
      setUsers(usersData);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUserGroups = async (userId: string) => {
    try {
      const response = await groupService.getGroups();
      const allGroups = getApiCollection<any>(response, 'loading groups for offboarding');
      // TODO: Filter groups where user is a member
      setUserGroups(allGroups.slice(0, 3)); // Placeholder
    } catch (err: any) {
      setError(err.message || 'Failed to load user groups');
    }
  };

  const handleUserSelect = (user: any) => {
    setSelectedUser(user);
    loadUserGroups(getProp(user, 'id'));
    setActiveStep(1);
  };

  const handleActionChange = (action: string) => {
    setOffboardingActions({
      ...offboardingActions,
      [action]: !offboardingActions[action as keyof typeof offboardingActions],
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffboardingActions({
      ...offboardingActions,
      [e.target.name]: e.target.value,
    });
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Disable account
      if (offboardingActions.disableAccount) {
        // TODO: await userService.updateUser(getProp(selectedUser, 'id'), { accountEnabled: false });
      }

      // TODO: Implement other offboarding actions
      // - Revoke licenses
      // - Remove from groups
      // - Reset password
      // - Hide from GAL
      // - Forward email

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to offboard user');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField fullWidth placeholder="Search users..." sx={{ mb: 2 }} />
            {usersLoading ? (
              <Box sx={styles.loadingContainer}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {users.slice(0, 20).map((user) => (
                  <ListItem
                    key={getProp(user, 'id')}
                    button
                    onClick={() => handleUserSelect(user)}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Person />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getProp(user, 'displayName')}
                      secondary={
                        <>
                          {getProp(user, 'userPrincipalName')}
                          <br />
                          {getProp(user, 'jobTitle') || 'No job title'}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography>
                    <strong>Display Name:</strong> {getProp(selectedUser, 'displayName')}
                  </Typography>
                  <Typography>
                    <strong>UPN:</strong> {getProp(selectedUser, 'userPrincipalName')}
                  </Typography>
                  <Typography>
                    <strong>Job Title:</strong> {getProp(selectedUser, 'jobTitle') || '-'}
                  </Typography>
                  <Typography>
                    <strong>Department:</strong> {getProp(selectedUser, 'department') || '-'}
                  </Typography>
                  <Typography>
                    <strong>Account Status:</strong>{' '}
                    {getProp(selectedUser, 'accountEnabled') ? 'Enabled' : 'Disabled'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Licenses
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography color="text.secondary">
                    {getProp(selectedUser, 'assignedLicenses')?.length || 0} license(s) assigned
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Groups
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography color="text.secondary">
                    {userGroups.length} group membership(s)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Warning:</strong> The following actions will be performed:
              </Typography>
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardingActions.disableAccount}
                      onChange={() => handleActionChange('disableAccount')}
                    />
                  }
                  label="Disable user account"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardingActions.revokeAllLicenses}
                      onChange={() => handleActionChange('revokeAllLicenses')}
                    />
                  }
                  label="Revoke all licenses"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardingActions.removeFromAllGroups}
                      onChange={() => handleActionChange('removeFromAllGroups')}
                    />
                  }
                  label="Remove from all groups"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardingActions.resetPassword}
                      onChange={() => handleActionChange('resetPassword')}
                    />
                  }
                  label="Reset password"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardingActions.hideFromGAL}
                      onChange={() => handleActionChange('hideFromGAL')}
                    />
                  }
                  label="Hide from Global Address List"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardingActions.forwardEmail}
                      onChange={() => handleActionChange('forwardEmail')}
                    />
                  }
                  label="Forward email to another user"
                />
              </Grid>
              {offboardingActions.forwardEmail && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Forward email to"
                    name="forwardEmailTo"
                    value={offboardingActions.forwardEmailTo}
                    onChange={handleInputChange}
                    placeholder="user@domain.com"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Alert severity="error" icon={<Warning />} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Confirm Offboarding</strong>
              </Typography>
              <Typography variant="body2">
                You are about to offboard <strong>{getProp(selectedUser, 'displayName')}</strong>.
                This action will perform the following:
              </Typography>
            </Alert>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Actions to be performed:
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {offboardingActions.disableAccount && (
                    <ListItem>
                      <ListItemText primary="✓ Disable user account" />
                    </ListItem>
                  )}
                  {offboardingActions.revokeAllLicenses && (
                    <ListItem>
                      <ListItemText primary="✓ Revoke all licenses" />
                    </ListItem>
                  )}
                  {offboardingActions.removeFromAllGroups && (
                    <ListItem>
                      <ListItemText primary="✓ Remove from all groups" />
                    </ListItem>
                  )}
                  {offboardingActions.resetPassword && (
                    <ListItem>
                      <ListItemText primary="✓ Reset password" />
                    </ListItem>
                  )}
                  {offboardingActions.hideFromGAL && (
                    <ListItem>
                      <ListItemText primary="✓ Hide from Global Address List" />
                    </ListItem>
                  )}
                  {offboardingActions.forwardEmail && (
                    <ListItem>
                      <ListItemText
                        primary={`✓ Forward email to ${offboardingActions.forwardEmailTo}`}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Box>
        <Box sx={styles.pageHeader}>
          <Box sx={styles.flexCenter}>
            <CheckCircle sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
            <Typography variant="h4">Offboarding Complete</Typography>
          </Box>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            User has been successfully offboarded
          </Typography>
          <Typography color="text.secondary" paragraph>
            {getProp(selectedUser, 'displayName')} ({getProp(selectedUser, 'userPrincipalName')})
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Offboard Another User
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Box sx={styles.flexCenter}>
          <PersonRemove sx={{ fontSize: 40, mr: 2, color: 'error.main' }} />
          <Typography variant="h4">Employee Offboarding</Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300, mb: 3 }}>{renderStepContent(activeStep)}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color={activeStep === steps.length - 1 ? 'error' : 'primary'}
            onClick={handleNext}
            disabled={loading || (activeStep === 0 && !selectedUser)}
            startIcon={
              loading ? (
                <CircularProgress size={20} />
              ) : activeStep === steps.length - 1 ? (
                <Save />
              ) : null
            }
          >
            {loading ? 'Processing...' : activeStep === steps.length - 1 ? 'Offboard User' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
